import type { FC, ReactNode } from 'react';
import React from 'react';

import { CrossSmallIcon } from '@setup/svg-icons/svg-icons';

import { CustomMuiChip } from './MuiChip.style';

import type { ChipProps } from '@mui/material';

/**
 * ChipUIPropsType represents the props for the MuiChip component.
 * @typedef {Object} ChipUIPropsType
 * @property {ReactNode} [icon] - Icon element to be displayed within the chip.
 * @property {string} label - The text label for the chip.
 * @property {Variant} [variantChip='default'] - The variant of the chip. Can be one of: 'default', 'info', 'permission', 'view', or 'edit'.
 * @property {boolean} [isHavePermission] - Indicates whether the user has permission(only garage)
 * @property {boolean} [isSelected] - Indicates whether the chip is selected.
 */

/**
 * Props for the MuiChip component, extending from MUI's `ChipProps`.
 * more detailed information in the documentation  ->
 *  https://mui.com/material-ui/api/chip/
 */

/**
 * MuiChip is a custom MuiChip component.
 * @param {ChipUIPropsType} props - The props for the MuiChip component.
 * @returns {JSX.Element} A React element representing the MuiChip.
 */

type Variant = 'default' | 'info' | 'permission' | 'view' | 'edit';
type ChipUIPropsType = Omit<ChipProps, 'variant'> & {
  label: string;
  icon?: ReactNode;
  variantChip?: Variant;
  isHavePermission?: boolean;
  isSelected?: boolean;
};

export const MuiChip: FC<ChipUIPropsType> = ({
  icon,
  label,
  variantChip = 'default',
  isHavePermission,
  isSelected,
  ...restProps
}) => {
  return (
    <CustomMuiChip
      isHavePermission={isHavePermission}
      icon={icon}
      label={label}
      hasLabel={!!label}
      className={variantChip}
      isSelected={isSelected}
      deleteIcon={<CrossSmallIcon id="deleteItemButton" />}
      {...restProps}
    />
  );
};
